import { useCallback, useEffect, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import { useRequestWithFeedback } from "../../../composites";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";

export const useFetchPaginatedPaymentActivity = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchPaymentActivity.Response,
    API.FetchPaymentActivity.Error
  >();

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (
      pageNumber: number,
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      overrideDefaultQueryString?: string,
      disableCaching?: boolean,
    ) => {
      return send({
        action: API.fetchPaymentActivity(
          offsetIndex,
          pageSize,
          queryString,
          overrideDefaultQueryString,
          disableCaching,
        ),
        onData: ({ results, page: { totalElements } }) => {
          dispatch(
            actions.upsertPage({
              pageNumber,
              results,
              total: totalElements,
            }),
          );
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPage,
    loading,
  });
};

export const useFetchPaymentActivity = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchPaymentActivity.Response,
    API.FetchPaymentActivity.Error
  >();

  const dispatch = useAppDispatch();

  const fetchNext = useCallback(
    (
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      disableCaching?: boolean,
    ) => {
      return send({
        action: API.fetchPaymentActivity(
          offsetIndex,
          pageSize,
          queryString,
          undefined,
          disableCaching,
        ),
        onData: ({ results, page: { totalElements } }) => {
          if (offsetIndex === 0) {
            dispatch(actions.replaceResults({ results, total: totalElements }));
          } else {
            dispatch(
              actions.appendResults({
                results,
                total: totalElements,
              }),
            );
          }
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchNext,
    loading,
  });
};

export const useFetchPaymentMetadataById = () => {
  const { send, loading, status } = useRequestWithFeedback<
    | API.FetchACHPayment.Response
    | API.FetchScheduledACHPayment.Response
    | API.FetchFedNowPayment.Response
    | API.FetchSwiftTransaction.Response
    | API.FetchWire.Response,
    | API.FetchACHPayment.Error
    | API.FetchScheduledACHPayment.Error
    | API.FetchFedNowPayment.Error
    | API.FetchSwiftTransaction.Error
    | API.FetchWire.Error
  >();

  const dispatch = useAppDispatch();

  const fetchPaymentMetadata = useCallback(
    (uuid: string, model: string) => {
      if (model === "FedNowTransaction") {
        return send({
          action: API.fetchFedNowById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            return data;
          },
        });
      }
      if (model === "SwiftWireTransaction") {
        return send({
          action: API.fetchSwiftWireById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            return data;
          },
        });
      }
      if (model === "UnverifiedACHTransfer") {
        return send({
          action: API.fetchTransferById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            return data;
          },
        });
      }
      if (model === "WireTransaction") {
        return send({
          action: API.fetchWireById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            return data;
          },
        });
      }
      return send({
        action: API.fetchTransactionById(uuid),
        onData: (data) => {
          dispatch(actions.updateSelected(data));
          return data;
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPaymentMetadata,
    loading,
  });
};

export const useFetchACHReversalById = () => {
  const { send, loading, status } = useRequestWithFeedback<API.ACHReversal>();

  const fetchACHReversalById = useCallback(
    (uuid: string) => {
      return send({
        action: API.fetchACHReversalById(uuid),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchACHReversalById,
    loading,
  });
};

export const useFetchPaymentActivityById = () => {
  const { send, loading, status } =
    useRequestWithFeedback<API.FetchSinglePaymentActivity.Response>();

  const fetchPaymentActivityById = useCallback(
    (
      uuid:
        | API.ScheduledACHPaymentId
        | API.ACHPaymentId
        | API.ACHReversalId
        | API.SwiftTransactionId
        | API.WireId,
    ) => {
      return send({
        action: API.fetchPaymentActivityById(uuid),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPaymentActivityById,
    loading,
  });
};

export const useFetchPaymentActivitiesByParent = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchPaymentActivity.Response,
    API.FetchPaymentActivity.Error
  >();

  const fetchPaymentActivitiesByParent = useCallback(
    (parentId: string) => {
      return send({
        action: API.fetchPaymentActivity(0, 20, `parent=${parentId}`, "", true),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPaymentActivitiesByParent,
    loading,
  });
};

// the max page size payment_activity allows in a single request
const BULK_PAYMENTS_FETCH_LIMIT = 100;

// fetches a bulk payment's payments
// uses the payment_activity bulk filter capped at the endpoint's max page size
// NOTE: bulks with more than BULK_PAYMENTS_FETCH_LIMIT payments are truncated
export const useFetchBulkPaymentPayments = (bulkPaymentId?: string) => {
  const { send, loading } = useRequestWithFeedback<
    API.FetchPaymentActivity.Response,
    API.FetchPaymentActivity.Error
  >();

  const [payments, setPayments] = useState<API.PaymentActivity[] | null>(null);

  useEffect(() => {
    if (!bulkPaymentId) {
      setPayments([]);
      return () => {};
    }

    let cancelled = false;
    setPayments(null);

    send({
      action: API.fetchPaymentActivity(
        0,
        BULK_PAYMENTS_FETCH_LIMIT,
        `bulk_payment=${bulkPaymentId}`,
      ),
      onData: ({ results }) => {
        if (!cancelled) {
          setPayments(results ?? []);
        }
      },
    }).catch(() => {
      if (!cancelled) {
        setPayments([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [bulkPaymentId, send]);

  return useObjectMemo({
    payments,
    loading,
  });
};

export const useReversePayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateACHReversal.Response,
    API.CreateACHReversal.Error
  >();

  const reversePayment = useCallback(
    (payload: API.CreateACHReversal.Request) => {
      return send({
        action: API.reversePayment(payload),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: reversePayment,
    loading,
  });
};
